import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import iconData from '../assets/images_base64_by_gender_tipo.json';

interface IconEntry {
  base64: string;
  sketch_url: string;
  full_url?: string;
}

interface IconData {
  Donna: Record<string, IconEntry>;
  Uomo: Record<string, IconEntry>;
}

interface BudgetConstraint {
  activity: number;
  capacity: number;
  slack: number;
  percentage_remaining: number;
}

interface BudgetConstraintWithItems {
  value: BudgetConstraint;
  items: BudgetConstraint;
}

interface TipoFilatoMetrics {
  // Capacity constraint (budget)
  budgetActivity: number;
  budgetCapacity: number | null;
  budgetCoverage: number | null;
  // Quantity constraint
  quantityActivity: number;
  quantityCapacity: number | null;
  quantityCoverage: number | null;
  // Unique models constraint
  uniqueModels: number;
  uniqueModelsTarget: number | null;
  modelsCoverage: number | null;
  // SKU count constraint (solo se il MP lo prevede)
  skuCount: number;
  skuCountTarget: number | null;
  skuCountCoverage: number | null;
  // Average price constraint
  averagePrice: number;
  targetPrice: number | null;
  priceCoverage: number | null;
}

// mpConstraints structure: { capacity: {...}, quantity: {...}, models_unique: {...}, sku_count: {...}, avg_price: {...} }
interface MpConstraints {
  capacity: Record<string, BudgetConstraint>;
  quantity: Record<string, BudgetConstraint>;
  models_unique: Record<string, BudgetConstraint>;
  // presente solo se il merchandising plan ha la colonna SKU COUNT
  sku_count?: Record<string, BudgetConstraint>;
  avg_price: Record<string, BudgetConstraint>;
  _meta?: {
    included_tipi_filato_by_mp?: Record<string, string[]>;
    linked_order_run?: {
      enabled?: boolean;
      run_id?: number | null;
      mp_scaled?: boolean;
      budget_scaled?: boolean;
      summary?: Record<string, unknown>;
    };
  };
}

interface ConstraintSwatchesProps {
  budgetConstraints: Record<string, BudgetConstraint | BudgetConstraintWithItems>;
  mpConstraints: MpConstraints;
  width?: number;
  height?: number;
  onTipoFilatoClick?: (tipoFilato: string) => void;
  onExplainConstraint?: (request: {
    targetType: 'budget' | 'tipo_filato';
    targetName: string;
    constraintType?: string;
    consegna?: string;
    tipoFilato?: string;
  }) => void;
  explanations?: Record<string, string>;  // key: "{targetType}_{targetName}" -> explanation text
}

export function ConstraintSwatches({
  budgetConstraints,
  mpConstraints,
  width = 400,
  height = 400,
  onTipoFilatoClick,
  onExplainConstraint,
  explanations,
}: ConstraintSwatchesProps) {
  const normalizeExplanationDisplay = (value: string): string => {
    const trimmed = value.trim();
    if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
      return value;
    }
    try {
      const parsed = JSON.parse(trimmed) as { text?: unknown };
      if (typeof parsed?.text === 'string') {
        return parsed.text;
      }
    } catch {
      return value;
    }
    return value;
  };

  const defaultBudgetConstraint: BudgetConstraint = {
    activity: 0,
    capacity: 0,
    slack: 0,
    percentage_remaining: 100,
  };

  const hasValueAndItems = (
    constraint: BudgetConstraint | BudgetConstraintWithItems
  ): constraint is BudgetConstraintWithItems => {
    return (
      !!constraint &&
      typeof constraint === 'object' &&
      'value' in constraint &&
      'items' in constraint
    );
  };

  const getValueConstraint = (
    constraint: BudgetConstraint | BudgetConstraintWithItems
  ): BudgetConstraint => {
    if (hasValueAndItems(constraint)) {
      return constraint.value || defaultBudgetConstraint;
    }
    return constraint || defaultBudgetConstraint;
  };

  const getItemsConstraint = (
    constraint: BudgetConstraint | BudgetConstraintWithItems
  ): BudgetConstraint | null => {
    if (hasValueAndItems(constraint)) {
      return constraint.items || defaultBudgetConstraint;
    }
    return null;
  };

  // State for tooltip
  const [hoveredSwatch, setHoveredSwatch] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [hoveredMetrics, setHoveredMetrics] = useState<TipoFilatoMetrics | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // State for explanation popover
  const [activeExplanation, setActiveExplanation] = useState<string | null>(null); // key: "{targetType}_{targetName}"
  const [explanationPos, setExplanationPos] = useState<{ x: number; y: number } | null>(null);
  const explanationRef = useRef<HTMLDivElement>(null);
  const mpMeta = mpConstraints?._meta;
  const isLinkedMpScaled = Boolean(mpMeta?.linked_order_run?.enabled && mpMeta?.linked_order_run?.mp_scaled);

  const handleExplainClick = useCallback((e: React.MouseEvent, request: {
    targetType: 'budget' | 'tipo_filato';
    targetName: string;
    constraintType?: string;
    consegna?: string;
    tipoFilato?: string;
  }) => {
    e.stopPropagation();
    const key = `${request.targetType}_${request.targetName}`;
    if (activeExplanation === key) {
      setActiveExplanation(null);
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setExplanationPos({ x: rect.right + 8, y: rect.top });
    setActiveExplanation(key);
    if (!explanations?.[key]) {
      onExplainConstraint?.(request);
    }
  }, [activeExplanation, explanations, onExplainConstraint]);

  // Dismiss popover on outside click
  useEffect(() => {
    if (!activeExplanation) return;
    const handler = (e: MouseEvent) => {
      if (explanationRef.current && !explanationRef.current.contains(e.target as Node)) {
        setActiveExplanation(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activeExplanation]);

  // Calculate swatch sizes relative to widget height
  const budgetSwatchCount = Object.keys(budgetConstraints || {}).length;
  const gap = 8; // gap-2 in Tailwind = 0.5rem = 8px
  const budgetLabelHeight = 20;
  const mpAnchorSize = height * 0.45; // Keeps tipo-filato cards stable
  const availableBudgetHeight = Math.max(0, height - budgetLabelHeight - gap * Math.max(0, budgetSwatchCount - 1));
  const budgetSwatchSize = budgetSwatchCount > 0
    ? Math.max(56, Math.min(mpAnchorSize, availableBudgetHeight / budgetSwatchCount))
    : mpAnchorSize;
  // Con il vincolo SKU COUNT le card hanno una metrica in piu' (5 righe invece di 4):
  // si passa da 4 a 3 righe di griglia, cosi' i quadratini sono piu' grandi e il
  // contenuto ci sta, mantenendo la stessa altezza complessiva del blocco.
  const hasSkuCountRow = Object.values(mpConstraints?.sku_count || {}).some(
    (constraint) => constraint?.capacity !== null && constraint?.capacity !== undefined && Number.isFinite(Number(constraint.capacity)),
  );
  const rightSwatchRows = hasSkuCountRow ? 3 : 4;
  const rightSwatchSize = (2 * mpAnchorSize - 2 * gap) / rightSwatchRows; // Square, aligned with historical MP layout

  const budgetLabelFontSize = Math.max(10, Math.min(16, budgetSwatchSize * 0.09));
  const budgetPercentageFontSize = Math.max(13, Math.min(22, budgetSwatchSize * 0.12));
  const rightLabelFontSize = rightSwatchSize * 0.12; // Name label for right swatches
  // con 5 metriche si stringe un filo la proporzione, restando comunque piu' grandi
  // in valore assoluto rispetto alle card a 4 righe
  const rightMetricFontRatio = hasSkuCountRow ? 0.10 : 0.13;
  const rightValueFontSize = rightSwatchSize * rightMetricFontRatio; // Value font for right swatches
  const rightEmojiSize = rightSwatchSize * rightMetricFontRatio; // Emoji size

  const toNullableNumber = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Calculate coverage percentage. Prefer activity/capacity (authoritative),
  // fallback to percentage_remaining when needed.
  const getCoverage = (constraint: BudgetConstraint | undefined): number | null => {
    if (!constraint) return 0;
    const capacity = toNullableNumber(constraint.capacity);
    if (capacity === null) return null; // target is missing (None/null)
    if (capacity === 0) return 0;
    const activity = toNullableNumber(constraint.activity);
    if (activity !== null) {
      return (activity / capacity) * 100;
    }
    const percentageRemaining = toNullableNumber(constraint.percentage_remaining);
    if (percentageRemaining !== null) {
      return 100 - percentageRemaining;
    }
    return 0;
  };

  // Treemap-inspired palette colors for constraints (brighter for better contrast)
  // Red (bad): #C4706A (brighter terracotta)
  // Yellow/neutral: #E8D8C8 (warm cream)
  // Green (good): #5A9A6A (brighter sage green)
  // Strong red (over limit): #A85450 (brighter dark red)

  // Get color based on coverage (stronger red if > 100%, lighter red if > 95%, green otherwise)
  const getSwatchColor = (coverage: number): string => {
    if (coverage > 100) return '#A85450'; // Bright dark red (over limit)
    if (coverage > 95) return '#D4A088'; // Brighter salmon
    return '#5A9A6A'; // Brighter sage green
  };

  // Get bar color for budget coverage - uses same scale
  const getBudgetBarColor = (coverage: number): string => {
    return getQuantityBarColor(coverage);
  };

  // Get bar color using treemap-inspired diverging scale: Red (0%) -> Cream (50%) -> Green (100%)
  const getQuantityBarColor = (coverage: number): string => {
    if (coverage > 100) return '#A85450'; // Bright dark red if over 100%

    // Clamp coverage between 0 and 100
    const clampedCoverage = Math.max(0, Math.min(100, coverage));

    // Brighter treemap-inspired diverging scale
    // Red: #C4706A - at 0%
    // Cream: #E8D8C8 - at 50%
    // Green: #5A9A6A - at 100%
    const red = { r: 196, g: 112, b: 106 };   // #C4706A
    const cream = { r: 232, g: 216, b: 200 }; // #E8D8C8
    const green = { r: 90, g: 154, b: 106 };  // #5A9A6A

    let r: number, g: number, b: number;

    if (clampedCoverage <= 50) {
      // Interpolate from red (0%) to cream (50%)
      const ratio = clampedCoverage / 50;
      r = Math.round(red.r + (cream.r - red.r) * ratio);
      g = Math.round(red.g + (cream.g - red.g) * ratio);
      b = Math.round(red.b + (cream.b - red.b) * ratio);
    } else {
      // Interpolate from cream (50%) to green (100%)
      const ratio = (clampedCoverage - 50) / 50;
      r = Math.round(cream.r + (green.r - cream.r) * ratio);
      g = Math.round(cream.g + (green.g - cream.g) * ratio);
      b = Math.round(cream.b + (green.b - cream.b) * ratio);
    }

    return `rgb(${r}, ${g}, ${b})`;
  };

  // Format price in Italian convention (€ X.XXX)
  const formatPrice = (price: number): string => {
    return '€ ' + Math.round(price).toLocaleString('it-IT');
  };

  // Get price deviation indicators (▲/▼) - using brighter palette colors
  const getPriceIndicators = (avgPrice: number, targetPrice: number | null): { arrows: string; color: string } => {
    if (targetPrice === null || targetPrice === 0) return { arrows: '', color: '#5A9A6A' }; // Bright sage green

    const deviation = ((avgPrice - targetPrice) / targetPrice) * 100;

    if (Math.abs(deviation) < 5) {
      return { arrows: '', color: '#5A9A6A' }; // Bright sage green, no arrows
    } else if (deviation > 0) {
      // Above target
      if (deviation > 20) return { arrows: '▲▲', color: '#A85450' }; // Bright dark red
      return { arrows: '▲', color: '#C4706A' }; // Brighter terracotta
    } else {
      // Below target
      if (deviation < -20) return { arrows: '▼▼', color: '#A85450' }; // Bright dark red
      return { arrows: '▼', color: '#C4706A' }; // Brighter terracotta
    }
  };

  // Get price color as gradient - green at target, red when far from target (in either direction)
  const getPriceColor = (avgPrice: number, targetPrice: number | null): string => {
    if (targetPrice === null || targetPrice === 0) return '#5A9A6A'; // Bright sage green if no target

    // Calculate absolute deviation percentage
    const deviation = Math.abs((avgPrice - targetPrice) / targetPrice) * 100;

    // Max deviation for full red (e.g., 30% deviation = full red)
    const maxDeviation = 30;

    // Clamp deviation between 0 and maxDeviation
    const clampedDeviation = Math.min(deviation, maxDeviation);

    // Ratio: 0 = at target (green), 1 = max deviation (red)
    const ratio = clampedDeviation / maxDeviation;

    // Interpolate from bright sage green (0% deviation) to terracotta (max deviation)
    // Green: #5A9A6A - rgb(90, 154, 106)
    // Red: #C4706A - rgb(196, 112, 106)
    const greenStart = { r: 90, g: 154, b: 106 };
    const redEnd = { r: 196, g: 112, b: 106 };

    const r = Math.round(greenStart.r + (redEnd.r - greenStart.r) * ratio);
    const g = Math.round(greenStart.g + (redEnd.g - greenStart.g) * ratio);
    const b = Math.round(greenStart.b + (redEnd.b - greenStart.b) * ratio);

    return `rgb(${r}, ${g}, ${b})`;
  };

  // Get icon for a tipo filato
  const getIcon = (tipoFilato: string): string | null => {
    const icons = iconData as IconData;

    // Check in Donna
    if (icons.Donna && icons.Donna[tipoFilato]) {
      return `data:image/png;base64,${icons.Donna[tipoFilato].base64}`;
    }

    // Check in Uomo
    if (icons.Uomo && icons.Uomo[tipoFilato]) {
      return `data:image/png;base64,${icons.Uomo[tipoFilato].base64}`;
    }

    return null;
  };

  // Build metrics for a tipo filato from all constraint types
  const buildMetrics = (tipoFilato: string): TipoFilatoMetrics => {
    const capacityConstraint = mpConstraints.capacity?.[tipoFilato];
    const quantityConstraint = mpConstraints.quantity?.[tipoFilato];
    const modelsConstraint = mpConstraints.models_unique?.[tipoFilato];
    const skuCountConstraint = mpConstraints.sku_count?.[tipoFilato];
    const priceConstraint = mpConstraints.avg_price?.[tipoFilato];

    const defaultConstraint: BudgetConstraint = { activity: 0, capacity: 0, slack: 0, percentage_remaining: 100 };

    const cap = capacityConstraint || defaultConstraint;
    const qty = quantityConstraint || defaultConstraint;
    const models = modelsConstraint || defaultConstraint;
    const skuCountMetric = skuCountConstraint || defaultConstraint;
    const price = priceConstraint || defaultConstraint;

    return {
      budgetActivity: toNullableNumber(cap.activity) ?? 0,
      budgetCapacity: toNullableNumber(capacityConstraint?.capacity),
      budgetCoverage: getCoverage(cap),
      quantityActivity: toNullableNumber(qty.activity) ?? 0,
      quantityCapacity: toNullableNumber(quantityConstraint?.capacity),
      quantityCoverage: getCoverage(qty),
      uniqueModels: toNullableNumber(models.activity) ?? 0,
      uniqueModelsTarget: toNullableNumber(modelsConstraint?.capacity),
      modelsCoverage: getCoverage(models),
      skuCount: toNullableNumber(skuCountMetric.activity) ?? 0,
      skuCountTarget: skuCountConstraint ? toNullableNumber(skuCountConstraint?.capacity) : null,
      skuCountCoverage: skuCountConstraint ? getCoverage(skuCountMetric) : null,
      averagePrice: toNullableNumber(price.activity) ?? 0,
      targetPrice: toNullableNumber(priceConstraint?.capacity),
      priceCoverage: getCoverage(price),
    };
  };

  // Check if all constraints are satisfied for a tipo filato
  const areAllConstraintsSatisfied = (metrics: TipoFilatoMetrics): boolean => {
    const budgetOk = metrics.budgetCoverage === null ? true : metrics.budgetCoverage <= 100;
    const quantityOk = metrics.quantityCoverage === null ? true : metrics.quantityCoverage <= 100;
    const modelsOk = metrics.modelsCoverage === null ? true : metrics.modelsCoverage >= 80; // At least 80% of target models
    const skuCountOk = metrics.skuCountCoverage === null ? true : metrics.skuCountCoverage <= 100; // SKU count e' un tetto
    const priceOk = metrics.priceCoverage === null ? true : (metrics.priceCoverage <= 120 && metrics.priceCoverage >= 80); // Price within 20% of target
    return budgetOk && quantityOk && modelsOk && skuCountOk && priceOk;
  };

  // Handle mouse movement for smart tooltip positioning (using viewport coordinates)
  const handleMouseMove = (e: React.MouseEvent, name: string, metrics: TipoFilatoMetrics) => {
    // Use viewport (fixed) positioning for tooltip to avoid container clipping
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Tooltip dimensions (approximate - increased for longer content)
    const tooltipWidth = 350;
    const tooltipHeight = 120;

    // Position tooltip just below and slightly right of cursor
    const offsetX = 12;
    const offsetY = 12;

    // Default: position below-right of cursor (using client coordinates for fixed positioning)
    let x = e.clientX + offsetX;
    let y = e.clientY + offsetY;

    // Adjust if tooltip would go outside viewport on right
    if (x + tooltipWidth > viewportWidth - 10) {
      x = e.clientX - tooltipWidth - offsetX; // Position to the left
    }

    // Adjust if tooltip would go outside viewport on bottom
    if (y + tooltipHeight > viewportHeight - 10) {
      y = e.clientY - tooltipHeight - offsetY; // Position above
    }

    // Ensure tooltip doesn't go beyond left/top edges
    x = Math.max(10, x);
    y = Math.max(10, y);

    setTooltipPosition({ x, y });
    setHoveredSwatch(name);
    setHoveredMetrics(metrics);
  };

  const handleMouseLeave = () => {
    setHoveredSwatch(null);
    setTooltipPosition(null);
    setHoveredMetrics(null);
  };

  // Gather all unique tipo filato names from all constraint types
  const sortedMpConstraints = useMemo(() => {
    const tipoFilatoSet = new Set<string>();

    // Collect all tipo filato names from each constraint type
    if (mpConstraints.capacity) {
      Object.keys(mpConstraints.capacity).forEach(name => tipoFilatoSet.add(name));
    }
    if (mpConstraints.quantity) {
      Object.keys(mpConstraints.quantity).forEach(name => tipoFilatoSet.add(name));
    }
    if (mpConstraints.sku_count) {
      Object.keys(mpConstraints.sku_count).forEach(name => tipoFilatoSet.add(name));
    }
    if (mpConstraints.models_unique) {
      Object.keys(mpConstraints.models_unique).forEach(name => tipoFilatoSet.add(name));
    }
    if (mpConstraints.avg_price) {
      Object.keys(mpConstraints.avg_price).forEach(name => tipoFilatoSet.add(name));
    }

    // Build metrics for each tipo filato
    const tipoFilatoMetrics = Array.from(tipoFilatoSet).map(name => ({
      name,
      metrics: buildMetrics(name),
    }));

    // Filter out those with zero capacity and sort by highest budget coverage first
    return tipoFilatoMetrics
      .filter(item =>
        item.metrics.budgetCapacity === null ||
        item.metrics.quantityCapacity === null ||
        (item.metrics.budgetCapacity ?? 0) > 0 ||
        (item.metrics.quantityCapacity ?? 0) > 0
      )
      .sort((a, b) => (b.metrics.budgetCoverage ?? -1) - (a.metrics.budgetCoverage ?? -1));
  }, [mpConstraints]);

  // Keep budget constraints in original order (don't sort)
  const sortedBudgetConstraints = useMemo(() => {
    return Object.entries(budgetConstraints);
  }, [budgetConstraints]);

  return (
    // Non piu' largo della colonna Vincoli: con molti tipi filato la griglia scorre al suo
    // interno invece di allargare il blocco, che centrato debordava su treemap e Negozi.
    <div className="flex flex-col min-w-0" style={{ maxWidth: `${width}px` }}>
      <div
        ref={containerRef}
        className="flex flex-row gap-4 relative min-w-0"
      >
        {/* Left side - CONSEGNA (Budget constraints) - Large squares - Only show if budget data exists */}
        {sortedBudgetConstraints.length > 0 && (
          <>
            <div className="flex flex-col gap-2 flex-shrink-0">
              <span className="text-xs font-semibold text-gray-700 uppercase text-center">
                Budget Consegna
              </span>
              <div className="flex flex-col gap-2">
                {sortedBudgetConstraints.map(([name, constraint]) => {
                  const valueConstraint = getValueConstraint(constraint);
                  const itemsConstraint = getItemsConstraint(constraint);
                  const coverage = getCoverage(valueConstraint);
                  const normalizedCoverage = coverage === null ? 0 : Math.max(0, coverage);
                  const fillSize = Math.min(Math.sqrt(normalizedCoverage / 100) * 100, 100); // Scale by sqrt for area
                  const coverageLabel = coverage === null ? 'N/D' : `${coverage.toFixed(0)}%`;
                  const valueActivity = toNullableNumber(valueConstraint.activity);
                  const valueCapacity = toNullableNumber(valueConstraint.capacity);
                  const itemsActivity = itemsConstraint ? toNullableNumber(itemsConstraint.activity) : null;
                  const itemsCapacity = itemsConstraint ? toNullableNumber(itemsConstraint.capacity) : null;

                  return (
                    <div
                      key={name}
                      className="relative group cursor-pointer bg-white border border-black flex items-center justify-center"
                      style={{
                        width: `${budgetSwatchSize}px`,
                        height: `${budgetSwatchSize}px`,
                      }}
                    >
                      {/* Filled square inside */}
                      <div
                        className="absolute"
                        style={{
                          width: `${fillSize}%`,
                          height: `${fillSize}%`,
                          backgroundColor: getSwatchColor(normalizedCoverage),
                        }}
                      />

                      {/* Label overlay */}
                      <div className="relative z-10 text-center">
                        <div
                          className="font-bold uppercase leading-tight text-gray-800"
                          style={{ fontSize: `${budgetLabelFontSize}px` }}
                        >
                          {name.replace('_', ' ')}
                        </div>
                        <div
                          className="font-bold mt-1 text-gray-800"
                          style={{ fontSize: `${budgetPercentageFontSize}px` }}
                        >
                          {coverageLabel}
                        </div>
                      </div>

                      {/* Tooltip on hover */}
                      <div
                        className="absolute hidden group-hover:block bg-black text-white text-xs px-3 py-2 rounded whitespace-nowrap shadow-lg"
                        style={{
                          top: '50%',
                          left: '105%',
                          transform: 'translateY(-50%)',
                          zIndex: 9999,
                        }}
                      >
                        <div className="font-bold">{name.replace('_', ' ').toUpperCase()} ({coverageLabel})</div>
                        <div className="flex items-center gap-2">
                          <span>
                            {(valueActivity ?? 0).toLocaleString('it-IT')}€ / {valueCapacity === null ? 'N/D' : `${valueCapacity.toLocaleString('it-IT')}€`}
                          </span>
                          {itemsConstraint && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-700 text-gray-100 text-[10px] font-semibold">
                              Items {(itemsActivity ?? 0).toLocaleString('it-IT')} / {itemsCapacity === null ? 'N/D' : itemsCapacity.toLocaleString('it-IT')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Explain badge */}
                      {onExplainConstraint && (
                        <div
                          className="absolute top-1 right-1 z-30 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
                          style={{
                            width: `${budgetSwatchSize * 0.14}px`,
                            height: `${budgetSwatchSize * 0.14}px`,
                            backgroundColor: '#E8D8C8',
                            fontSize: `${budgetSwatchSize * 0.08}px`,
                            color: '#5b4636',
                            fontStyle: 'italic',
                            fontWeight: 700,
                          }}
                          onClick={(e) =>
                            handleExplainClick(e, {
                              targetType: 'budget',
                              targetName: name,
                              constraintType: 'budget',
                              consegna: name,
                            })
                          }
                        >
                          i
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Divider */}
            <div className="w-px bg-gray-300 flex-shrink-0" />
          </>
        )}

        {/* Right side - TIPI FILATO (MP constraints) - Card swatches with 3 metrics */}
        <div className="flex flex-col gap-2 flex-grow min-w-0">
          {/* Only show "Tipi Filato" label when budget constraints are also shown */}
          {sortedBudgetConstraints.length > 0 && (
            <span className="text-xs font-semibold text-gray-700 uppercase text-center">
              Tipi Filato
            </span>
          )}
          <div
            className="grid gap-2 min-w-0 overflow-x-auto"
            style={{
              gridTemplateRows: `repeat(${rightSwatchRows}, ${rightSwatchSize}px)`,
              gridAutoFlow: 'column',
              gridAutoColumns: `${rightSwatchSize}px`
            }}
          >
            {sortedMpConstraints.map(({ name, metrics }) => {
              const priceInfo = getPriceIndicators(metrics.averagePrice, metrics.targetPrice);
              const isBudgetTargetMissing = metrics.budgetCapacity === null;
              const isQuantityTargetMissing = metrics.quantityCapacity === null;
              const isModelsTargetMissing = metrics.uniqueModelsTarget === null;
              const isPriceTargetMissing = metrics.targetPrice === null;
              const modelsTarget = metrics.uniqueModelsTarget ?? 0;
              const hasSkuCountConstraint = metrics.skuCountTarget !== null;
              const skuCountCoverageCalc = metrics.skuCountCoverage ?? 0;
              const skuCountBarWidth = Math.min(skuCountCoverageCalc, 100);

              // Calculate bar widths (capped at 100%)
              const budgetBarWidth = Math.min(metrics.budgetCoverage ?? 0, 100);
              const quantityBarWidth = Math.min(metrics.quantityCoverage ?? 0, 100);
              const modelsCoverageCalc = modelsTarget > 0
                ? (metrics.uniqueModels / modelsTarget) * 100
                : 0;
              const modelsBarWidth = Math.min(modelsCoverageCalc, 100);

              // Bar height - thin bars
              const barHeight = Math.max(rightSwatchSize * 0.05, 4);
              const includedTipoFilati = mpMeta?.included_tipi_filato_by_mp?.[name] || [];

              return (
                <div
                  key={name}
                  className="relative cursor-pointer bg-white flex flex-col flex-shrink-0 border border-black"
                  style={{
                    width: `${rightSwatchSize}px`,
                    height: `${rightSwatchSize}px`,
                  }}
                  onClick={() => onTipoFilatoClick?.(name)}
                  onMouseMove={(e) => handleMouseMove(e, name, metrics)}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Inner colored border - attached to outer, overlaps title */}
                  <div
                    className="absolute inset-0 pointer-events-none z-20"
                    style={{
                      // border: '1px solid #000000',
                      boxShadow: 'inset 0 0 0 0.1px #000',
                    }}
                  />

                  {/* Explain badge */}
                  {onExplainConstraint && (
                    <div
                      className="absolute top-0.5 right-0.5 z-30 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
                      style={{
                        width: `${rightSwatchSize * 0.14}px`,
                        height: `${rightSwatchSize * 0.14}px`,
                        backgroundColor: '#E8D8C8',
                        fontSize: `${rightSwatchSize * 0.08}px`,
                        color: '#5b4636',
                        fontStyle: 'italic',
                        fontWeight: 700,
                      }}
                      onClick={(e) =>
                        handleExplainClick(e, {
                          targetType: 'tipo_filato',
                          targetName: name,
                          constraintType: 'tipo_filato',
                          tipoFilato: name,
                        })
                      }
                    >
                      i
                    </div>
                  )}

                  {/* Header with name only */}
                  <div
                    className="bg-white text-gray-800 font-bold px-1 flex items-center justify-center leading-tight relative border-b border-gray-100"
                    style={{
                      fontSize: `${rightLabelFontSize}px`,
                      minHeight: `${rightSwatchSize * 0.18}px`,
                      paddingTop: '2px',
                    }}
                  >
                    <span className="truncate uppercase">
                      {name.length > 10 ? name.substring(0, 9) + '.' : name}
                    </span>
                  </div>

                  {/* Metrics rows - tighter spacing */}
                  <div className="flex-1 flex flex-col justify-start pt-0.5 px-1.5 pb-1.5 relative" style={{ gap: '0px' }}>
                    {/* Row 1: Budget consumption */}
                    <div className="flex items-center gap-0.5">
                      <span
                        className={`font-bold ${isBudgetTargetMissing ? 'text-red-600' : 'opacity-0'}`}
                        style={{ fontSize: `${rightValueFontSize}px`, lineHeight: 1, width: `${rightValueFontSize * 0.7}px`, textAlign: 'center' }}
                      >
                        !
                      </span>
                      <span style={{ fontSize: `${rightEmojiSize}px`, lineHeight: 1, width: `${rightEmojiSize}px`, textAlign: 'center' }}>🎯</span>
                      <div
                        className={`flex-1 ${isBudgetTargetMissing ? 'opacity-0' : 'bg-gray-100'} overflow-hidden`}
                        style={{ height: `${barHeight}px` }}
                      >
                        <div
                          className="h-full"
                          style={{
                            width: `${budgetBarWidth}%`,
                            backgroundColor: getBudgetBarColor(metrics.budgetCoverage ?? 0),
                          }}
                        />
                      </div>
                      <span
                        className={`font-bold text-right flex-shrink-0 ${isBudgetTargetMissing ? 'opacity-0' : ''}`}
                        style={{
                          fontSize: `${rightValueFontSize}px`,
                          color: getBudgetBarColor(metrics.budgetCoverage ?? 0),
                          minWidth: `${rightValueFontSize * 2.3}px`,
                        }}
                      >
                        {(metrics.budgetCoverage ?? 0).toFixed(0)}%
                      </span>
                    </div>

                    {/* Row 2: Quantity consumption */}
                    <div className="flex items-center gap-0.5">
                      <span
                        className={`font-bold ${isQuantityTargetMissing ? 'text-red-600' : 'opacity-0'}`}
                        style={{ fontSize: `${rightValueFontSize}px`, lineHeight: 1, width: `${rightValueFontSize * 0.7}px`, textAlign: 'center' }}
                      >
                        !
                      </span>
                      <span style={{ fontSize: `${rightEmojiSize}px`, lineHeight: 1, width: `${rightEmojiSize}px`, textAlign: 'center' }}>👜</span>
                      <div
                        className={`flex-1 ${isQuantityTargetMissing ? 'opacity-0' : 'bg-gray-100'} overflow-hidden`}
                        style={{ height: `${barHeight}px` }}
                      >
                        <div
                          className="h-full"
                          style={{
                            width: `${quantityBarWidth}%`,
                            backgroundColor: getQuantityBarColor(metrics.quantityCoverage ?? 0),
                          }}
                        />
                      </div>
                      <span
                        className={`font-bold text-right flex-shrink-0 ${isQuantityTargetMissing ? 'opacity-0' : ''}`}
                        style={{
                          fontSize: `${rightValueFontSize}px`,
                          color: getQuantityBarColor(metrics.quantityCoverage ?? 0),
                          minWidth: `${rightValueFontSize * 2.3}px`,
                        }}
                      >
                        {(metrics.quantityCoverage ?? 0).toFixed(0)}%
                      </span>
                    </div>

                    {/* Row 3: Unique models */}
                    <div className="flex items-center gap-0.5">
                      <span
                        className={`font-bold ${isModelsTargetMissing ? 'text-red-600' : 'opacity-0'}`}
                        style={{ fontSize: `${rightValueFontSize}px`, lineHeight: 1, width: `${rightValueFontSize * 0.7}px`, textAlign: 'center' }}
                      >
                        !
                      </span>
                      <span style={{ fontSize: `${rightEmojiSize}px`, lineHeight: 1, width: `${rightEmojiSize}px`, textAlign: 'center' }}>📦</span>
                      <div
                        className={`flex-1 ${isModelsTargetMissing ? 'opacity-0' : 'bg-gray-100'} overflow-hidden`}
                        style={{ height: `${barHeight}px` }}
                      >
                        <div
                          className="h-full"
                          style={{
                            width: `${modelsBarWidth}%`,
                            backgroundColor: getQuantityBarColor(modelsCoverageCalc),
                          }}
                        />
                      </div>
                      <span
                        className={`font-bold text-right flex-shrink-0 ${isModelsTargetMissing ? 'opacity-0' : ''}`}
                        style={{
                          fontSize: `${rightValueFontSize}px`,
                          color: getQuantityBarColor(modelsCoverageCalc),
                          minWidth: `${rightValueFontSize * 2.3}px`,
                        }}
                      >
                        {modelsCoverageCalc.toFixed(0)}%
                      </span>
                    </div>

                    {/* Row 3b: SKU count (solo se il MP prevede il vincolo) */}
                    {hasSkuCountConstraint ? (
                      <div className="flex items-center gap-0.5">
                        <span
                          className="font-bold opacity-0"
                          style={{ fontSize: `${rightValueFontSize}px`, lineHeight: 1, width: `${rightValueFontSize * 0.7}px`, textAlign: 'center' }}
                        >
                          !
                        </span>
                        <span style={{ fontSize: `${rightEmojiSize}px`, lineHeight: 1, width: `${rightEmojiSize}px`, textAlign: 'center' }}>👕</span>
                        <div
                          className="flex-1 bg-gray-100 overflow-hidden"
                          style={{ height: `${barHeight}px` }}
                        >
                          <div
                            className="h-full"
                            style={{
                              width: `${skuCountBarWidth}%`,
                              backgroundColor: getQuantityBarColor(skuCountCoverageCalc),
                            }}
                          />
                        </div>
                        <span
                          className="font-bold text-right flex-shrink-0"
                          style={{
                            fontSize: `${rightValueFontSize}px`,
                            color: getQuantityBarColor(skuCountCoverageCalc),
                            minWidth: `${rightValueFontSize * 2.3}px`,
                          }}
                        >
                          {skuCountCoverageCalc.toFixed(0)}%
                        </span>
                      </div>
                    ) : null}

                    {/* Row 4: Average price - gradient color based on deviation from target */}
                    <div className="flex items-center gap-0.5">
                      <span
                        className={`font-bold ${isPriceTargetMissing ? 'text-red-600' : 'opacity-0'}`}
                        style={{ fontSize: `${rightValueFontSize}px`, lineHeight: 1, width: `${rightValueFontSize * 0.7}px`, textAlign: 'center' }}
                      >
                        !
                      </span>
                      <span style={{ fontSize: `${rightEmojiSize}px`, lineHeight: 1, width: `${rightEmojiSize}px`, textAlign: 'center' }}>🏷️</span>
                      <span
                        className="font-bold flex-shrink-0"
                        style={{
                          fontSize: `${rightValueFontSize}px`,
                          color: getPriceColor(metrics.averagePrice, metrics.targetPrice),
                        }}
                      >
                        {formatPrice(metrics.averagePrice)}
                      </span>
                      {priceInfo.arrows && (
                        priceInfo.arrows.length > 1 ? (
                          <span
                            className="font-bold flex-shrink-0"
                            style={{
                              fontSize: `${rightValueFontSize * 0.85}px`,
                              color: getPriceColor(metrics.averagePrice, metrics.targetPrice),
                              letterSpacing: '-0.5em',
                              textShadow: '-1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white',
                            }}
                          >
                            {priceInfo.arrows}
                          </span>
                        ) : (
                          <span
                            className="font-bold flex-shrink-0"
                            style={{
                              fontSize: `${rightValueFontSize * 0.85}px`,
                              color: getPriceColor(metrics.averagePrice, metrics.targetPrice),
                            }}
                          >
                            {priceInfo.arrows}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Explanation popover */}
        {activeExplanation && explanationPos && (
          <div
            ref={explanationRef}
            className="bg-white border border-gray-300 text-gray-800 text-xs px-4 py-3 rounded-lg shadow-xl"
            style={{
              position: 'fixed',
              left: `${Math.min(explanationPos.x, window.innerWidth - 340)}px`,
              top: `${Math.min(explanationPos.y, window.innerHeight - 200)}px`,
              zIndex: 100000,
              maxWidth: '320px',
              minWidth: '200px',
            }}
          >
            <div className="font-bold text-sm mb-1 text-gray-900">
              {activeExplanation.replace(/^(budget|tipo_filato)_/, '').replace('_', ' ').toUpperCase()}
            </div>
            {explanations?.[activeExplanation] ? (
              <div className="whitespace-pre-wrap leading-relaxed">
                {normalizeExplanationDisplay(explanations[activeExplanation])}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-gray-500">
                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                Generazione spiegazione...
              </div>
            )}
          </div>
        )}

        {/* Smart positioned tooltip - uses fixed positioning to avoid container clipping */}
        {hoveredSwatch && tooltipPosition && hoveredMetrics && (
          <div
            className="bg-black text-white text-xs px-3 py-2 rounded pointer-events-none shadow-lg flex items-start gap-2"
            style={{
              position: 'fixed',
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              whiteSpace: 'nowrap',
              zIndex: 99999,
            }}
          >
            {/* Icon in white rounded square */}
            {(() => {
              const iconUrl = getIcon(hoveredSwatch);
              return iconUrl ? (
                <div className="bg-white rounded p-1 flex-shrink-0">
                  <img
                    src={iconUrl}
                    alt={hoveredSwatch}
                    className="object-contain"
                    style={{
                      width: '32px',
                      height: '32px',
                      filter: 'brightness(0)',
                      opacity: 0.8,
                    }}
                  />
                </div>
              ) : null;
            })()}
            <div>
              <div className="font-bold">{hoveredSwatch.toUpperCase()}</div>
              {isLinkedMpScaled && (
                <div className="text-[10px] text-gray-300">Capacità MP residua dopo run ordini #{mpMeta?.linked_order_run?.run_id}</div>
              )}
              {(() => {
                const includedTipoFilati = mpMeta?.included_tipi_filato_by_mp?.[hoveredSwatch] || [];
                return includedTipoFilati.length > 0 && !(includedTipoFilati.length === 1 && includedTipoFilati[0] === hoveredSwatch) ? (
                  <div className="text-[10px] text-gray-300">Include DS: {includedTipoFilati.join(', ')}</div>
                ) : null;
              })()}
              <div>
                {isLinkedMpScaled ? 'Capacità residua' : 'Capacità'}: {hoveredMetrics.budgetActivity.toLocaleString('it-IT')}€ / {hoveredMetrics.budgetCapacity === null ? 'N/D' : `${hoveredMetrics.budgetCapacity.toLocaleString('it-IT')}€`}
                {hoveredMetrics.budgetCoverage === null ? ' (target nullo)' : ` (${hoveredMetrics.budgetCoverage.toFixed(0)}%)`}
              </div>
              <div>
                Quantità: {hoveredMetrics.quantityActivity.toLocaleString('it-IT')} / {hoveredMetrics.quantityCapacity === null ? 'N/D' : hoveredMetrics.quantityCapacity.toLocaleString('it-IT')}
                {hoveredMetrics.quantityCoverage === null ? ' (target nullo)' : ` (${hoveredMetrics.quantityCoverage.toFixed(0)}%)`}
              </div>
              <div>
                Modelli: {hoveredMetrics.uniqueModels} / {hoveredMetrics.uniqueModelsTarget === null ? 'N/D' : hoveredMetrics.uniqueModelsTarget} target
                {hoveredMetrics.modelsCoverage === null ? ' (target nullo)' : ` (${hoveredMetrics.modelsCoverage.toFixed(0)}%)`}
              </div>
              {hoveredMetrics.skuCountTarget !== null ? (
                <div>
                  SKU: {hoveredMetrics.skuCount} / {hoveredMetrics.skuCountTarget} target
                  {hoveredMetrics.skuCountCoverage === null ? '' : ` (${hoveredMetrics.skuCountCoverage.toFixed(0)}%)`}
                </div>
              ) : null}
              <div>
                Prezzo: {formatPrice(hoveredMetrics.averagePrice)} (target: {hoveredMetrics.targetPrice === null ? 'N/D' : formatPrice(hoveredMetrics.targetPrice)})
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConstraintSwatches;
